import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { NumericRange, NumericRangeType } from '@decisions/server/DecisionsFramework/Design/Page/Filters';
import './data-filter-fields.less';
import { RangePickerField } from './RangePickerField';
import { DataFilterField, IDataFilterFieldCommonProps } from './DataFilterField';
import { parseInputToNumber } from 'utils/NumericFormattingUtils';
import './NumericRangeField.less';
import debounce from 'debounce';

export interface INumericRangeFieldProps extends IDataFilterFieldCommonProps<NumericRange> {}

interface IEditingRangeStrings {
  above: string;
  below: string;
  equals: string;
}

interface INumericRangeFieldState {
  value: NumericRange;
  fieldStrings: IEditingRangeStrings;
}

enum NumRangeActions {
  MatchType,
  Value,
  Above,
  Below,
}

/**
 * Input for modifying a Numeric Range input.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function NumericRangeField({ onChange, personalization, validation, value }: INumericRangeFieldProps) {
  const [state, dispatch] = useReducer(numericRangeReducer, {
    value,
    fieldStrings: getStringsFromRange(value),
  });

  const onMinValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: NumRangeActions.Above, value: e.target.value });
  };
  const onMaxValueChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({ type: NumRangeActions.Below, value: e.target.value });
  const onEqualsValueChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({ type: NumRangeActions.Value, value: e.target.value });

  const onFieldChange = () => {};

  const { MatchType: rangeType } = value;

  const onValueChange = useCallback(() => {
    const range = fillRangeFromStrings(state.fieldStrings, state.value);
    onChange({ ...range, MatchType: rangeType });
  }, [onChange, rangeType, state.fieldStrings, state.value]);

  const emitValueChange = useMemo(() => debounce(onValueChange, 300), [onValueChange]);

  useEffect(() => {
    emitValueChange();
    return () => {
      emitValueChange.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value]);

  return (
    <DataFilterField
      value={value}
      fieldText={''}
      onChange={onValueChange}
      onFieldChange={onFieldChange}
      validation={validation}
      personalization={personalization}
    >
      {({ keyHandler }) => (
        <div className="dp-numeric-range-field__fields">
          <RangePickerField
            label="Min"
            onChange={onMinValueChange}
            value={state.fieldStrings.above}
            show={rangeType === NumericRangeType.Between}
            onKeyDown={keyHandler}
          />
          <RangePickerField
            label="Max"
            onChange={onMaxValueChange}
            value={state.fieldStrings.below}
            show={rangeType === NumericRangeType.Between}
            onKeyDown={keyHandler}
          />
          <RangePickerField
            onChange={onEqualsValueChange}
            value={state.fieldStrings.equals}
            show={
              rangeType === NumericRangeType.Above ||
              rangeType === NumericRangeType.Below ||
              rangeType === NumericRangeType.Equals
            }
            onKeyDown={keyHandler}
          />
        </div>
      )}
    </DataFilterField>
  );
}

function numericRangeReducer(
  state: INumericRangeFieldState,
  action: { type: NumRangeActions; value: any }
): INumericRangeFieldState {
  // will need to do some value checks here, unless we want them all server side,
  // but this will get us going.
  let value: NumericRange;
  let fieldStrings: IEditingRangeStrings;
  switch (action.type) {
    case NumRangeActions.MatchType:
      value = { ...state.value, MatchType: action.value };
      return { ...state, value };

    case NumRangeActions.Value:
      fieldStrings = {
        ...state.fieldStrings,
        equals: parseInputToNumber(action.value, true) ?? '',
      };
      return {
        fieldStrings,
        value: { ...state.value, Value: parseFloat(action.value) },
      };

    case NumRangeActions.Above:
      let Above = parseFloat(action.value);
      let above = parseInputToNumber(action.value) ?? '';

      fieldStrings = { ...state.fieldStrings, above };
      Above = rangeCheck(Above, state.value.MinAllowed, state.value.MaxAllowed);
      Above =
        state.value.MatchType === NumericRangeType.Between && Above > state.value.Below ? state.value.Below - 1 : Above;
      value = { ...state.value, Above };
      console.log(value);
      return { value, fieldStrings };

    case NumRangeActions.Below:
      let Below = action.value;
      let below = parseInputToNumber(action.value) ?? '';
      fieldStrings = { ...state.fieldStrings, below };
      rangeCheck(Below, state.value.MinAllowed, state.value.MaxAllowed);
      Below =
        state.value.MatchType === NumericRangeType.Between && Below > state.value.Above ? state.value.Above + 1 : Below;
      value = { ...state.value, Below: parseFloat(action.value) };
      return { ...state, value, fieldStrings };

    default:
      return state;
  }
}

function rangeCheck(value: number, minAllowed: number | null, maxAllowed: number | null) {
  value = minAllowed !== null ? Math.max(value, minAllowed) : value;
  value = maxAllowed !== null ? Math.min(value, maxAllowed) : value;
  return value;
}

function getStringsFromRange({ Above, Below, Value }: NumericRange) {
  return {
    above: Above.toString(),
    below: Below.toString(),
    equals: Value.toString(),
  };
}

function fillRangeFromStrings({ above, below, equals }: IEditingRangeStrings, range: NumericRange) {
  range = { ...range };
  range.Above = parseFloat(above);
  range.Below = parseFloat(below);
  range.Value = parseFloat(equals);
  return range;
}
